import React, { useCallback, useState } from 'react';
import {
  ListItemContainer,
  ListItemContent,
  ListItemTitle,
  ListItemPreview,
  ListItemMeta,
  ListItemMetaText,
  ListItemTags,
  Tag,
  DeleteButton
} from './DocumentListItem.styled';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from '../../elements/v2/modals/ConfirmationModal';

/**
 * Document List Item Component
 * Displays a single document in the dropdown list
 */
const DocumentListItem = ({ document, onSelect, onDelete }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const handleClick = useCallback((e) => {
    // Prevent triggering if clicking delete button
    if (e.target.closest('button')) return;
    
    onSelect?.(document);
  }, [document, onSelect]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onDelete?.(document.id);
    setShowDeleteModal(false);
  }, [document.id, onDelete]);

  const formatDate = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return new Date(date).toLocaleDateString();
  };

  return (
    <ListItemContainer onClick={handleClick}>
      <ListItemContent>
        <ListItemTitle>{document.title}</ListItemTitle>
        
        {document.preview && (
          <ListItemPreview>{document.preview}</ListItemPreview>
        )}
        
        <ListItemMeta>
          <ListItemMetaText>
            {document.wordCount} words • {formatDate(document.lastEditedAt)}
          </ListItemMetaText>
        </ListItemMeta>
        
        {document.tags && document.tags.length > 0 && (
          <ListItemTags>
            {document.tags.slice(0, 3).map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
            {document.tags.length > 3 && (
              <ListItemMetaText>+{document.tags.length - 3}</ListItemMetaText>
            )}
          </ListItemTags>
        )}
      </ListItemContent>
      
      <DeleteButton onClick={handleDelete} title="Delete document">
        <DeleteIcon style={{ fontSize: '18px' }} />
      </DeleteButton>
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Document?"
        message={`Are you sure you want to delete "${document.title}"? This action cannot be undone.`}
        icon="warning"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        onConfirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </ListItemContainer>
  );
};

export default DocumentListItem;

